
import React from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Send, ImagePlus, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PendingImageUpload } from '@/types/chat';
import ImageAttachmentArea from './ImageAttachmentArea';

interface ChatInputProps {
  messageText: string;
  sending: boolean;
  loadingMessages: boolean;
  initializing: boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  onTextareaChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onTextareaBlur: () => void;
  onSendMessage: () => void;
  // Image upload props
  pendingImages?: PendingImageUpload[];
  onAddImages?: (files: File[]) => void;
  onRemoveImage?: (index: number) => void;
  showImageUpload?: boolean;
  onToggleImageUpload?: () => void;
}

const ChatInput: React.FC<ChatInputProps> = ({
  messageText,
  sending,
  loadingMessages,
  initializing,
  textareaRef,
  onTextareaChange,
  onKeyDown,
  onTextareaBlur,
  onSendMessage,
  pendingImages = [],
  onAddImages,
  onRemoveImage,
  showImageUpload = false,
  onToggleImageUpload
}) => {
  const isDisabled = sending || loadingMessages || initializing;
  const hasContent = messageText.trim() || pendingImages.length > 0;
  const canSend = hasContent && !isDisabled;

  return (
    <div className="p-4 border-t">
      {/* Image upload area */}
      {showImageUpload && onAddImages && onRemoveImage && (
        <ImageAttachmentArea
          pendingImages={pendingImages}
          uploading={false}
          onAddImages={onAddImages}
          onRemoveImage={onRemoveImage}
          disabled={isDisabled}
        />
      )}

      <div className="flex space-x-2">
        <div className="flex-1 relative">
          <Textarea
            ref={textareaRef}
            placeholder="Type a message..."
            value={messageText}
            onChange={onTextareaChange}
            onKeyDown={onKeyDown}
            onBlur={onTextareaBlur}
            className={cn(
              "resize-none min-h-[40px] max-h-[150px] pr-10",
              "chat-textarea"
            )}
            disabled={isDisabled}
            style={{ fontSize: '16px' }}
          />
          
          {/* Image toggle button */}
          {onToggleImageUpload && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1 h-8 w-8"
              onClick={onToggleImageUpload}
              disabled={isDisabled}
            >
              <ImagePlus className={cn(
                "h-4 w-4",
                showImageUpload && "text-primary"
              )} />
            </Button>
          )}
        </div>

        <Button 
          onClick={onSendMessage} 
          size="icon" 
          disabled={!canSend}
          className="shrink-0"
        >
          {sending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
};

export default ChatInput;
